import { makeVar, useReactiveVar } from "@apollo/client";
import { Bookmarks as BookmarksIcon, MenuBook, PlaylistPlay, StickyNote2 } from "@mui/icons-material";
import { useCallback, useEffect, useMemo } from "react";

import { usePersistedSettings } from "../../hooks/usePersistedSettings";
import { useTestMode } from "../../hooks/useTestMode";
import useTranslation from "../../hooks/useTranslation";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { telemetryChannelName } from "../../util/constants";
import { Bookmarks } from "../Bookmarks/Bookmarks";
import { CoursebookSidebar } from "../Coursebook/CoursebookSidebar";
import { Notes } from "../Notes/Notes";
import { Outline } from "../Outline/Outline";

const tabsVar = makeVar(0);

export const icons = {
  PlaylistPlay,
  MenuBook,
  StickyNote2,
  Bookmarks: BookmarksIcon,
};

export const featureName = "tab";
export const changeActionType = "change";

export function useTabs(): {
  tabIdx: number;
  changeTab: (newTabIdx: number) => void;
  tabs: {
    label: string;
    component: (props: { active: boolean }) => React.ReactElement | null;
    icon: keyof typeof icons | null;
  }[];
} {
  const { translate } = useTranslation();
  const { courseTab, setCourseTab } = usePersistedSettings();
  const { testMode } = useTestMode();

  const tabs: {
    label: string;
    component: (props: { active: boolean }) => React.ReactElement | null;
    icon: keyof typeof icons | null;
  }[] = useMemo(
    () => [
      {
        label: translate("Outline"),
        component: Outline,
        icon: "PlaylistPlay",
      },
      {
        label: translate("Coursebook"),
        component: CoursebookSidebar,
        icon: "MenuBook",
      },
      {
        label: translate("Notes"),
        component: Notes,
        icon: "StickyNote2",
      },
      {
        label: translate("Bookmarks"),
        component: Bookmarks,
        icon: "Bookmarks",
      },
    ],
    [translate],
  );

  useEffect(() => {
    tabsVar(testMode ? 1 : courseTab);
  }, [courseTab, testMode]);

  const tabIdx = useReactiveVar(tabsVar);
  const changeTab = useCallback(
    (newTabIdx: number) => {
      if (testMode) return;
      tabsVar(newTabIdx);
      setCourseTab(newTabIdx);

      const tabName = tabs[newTabIdx]?.label?.toLowerCase() || "";
      createTelemetryEvent({
        feature: featureName,
        action: changeActionType,
        action_meta: tabName,
      }).fire(telemetryChannelName);
    },
    [setCourseTab, testMode, tabs],
  );

  return { tabIdx, changeTab, tabs };
}
